import React, { useState, useEffect, useRef } from 'react';

const SpeedRacerMath = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [carPosition, setCarPosition] = useState(50);
  const [obstacles, setObstacles] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [speed, setSpeed] = useState(5);
  const [distance, setDistance] = useState(0);
  const [lives, setLives] = useState(3);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const gameLoopRef = useRef(null);

  const generateQuestion = () => {
    const operations = [
      { type: 'multiply', symbol: '×' },
      { type: 'divide', symbol: '÷' },
      { type: 'add', symbol: '+' },
      { type: 'subtract', symbol: '-' },
      { type: 'fraction', symbol: 'of' }
    ];
    
    const op = operations[Math.floor(Math.random() * operations.length)];
    let num1, num2, correctAnswer, question;

    switch(op.type) {
      case 'multiply':
        num1 = Math.floor(Math.random() * 20) + 10;
        num2 = Math.floor(Math.random() * 12) + 2;
        correctAnswer = num1 * num2;
        question = `${num1} × ${num2}`;
        break;
      case 'divide':
        num2 = Math.floor(Math.random() * 12) + 2;
        correctAnswer = Math.floor(Math.random() * 20) + 5;
        num1 = num2 * correctAnswer;
        question = `${num1} ÷ ${num2}`;
        break;
      case 'add':
        num1 = Math.floor(Math.random() * 500) + 100;
        num2 = Math.floor(Math.random() * 500) + 100;
        correctAnswer = num1 + num2;
        question = `${num1} + ${num2}`;
        break;
      case 'subtract':
        num1 = Math.floor(Math.random() * 500) + 200;
        num2 = Math.floor(Math.random() * (num1 - 100)) + 50;
        correctAnswer = num1 - num2;
        question = `${num1} - ${num2}`;
        break;
      case 'fraction':
        const fractions = ['1/2', '1/4', '3/4', '2/3', '1/3'];
        const frac = fractions[Math.floor(Math.random() * fractions.length)];
        num1 = Math.floor(Math.random() * 100) + 20;
        const [numerator, denominator] = frac.split('/').map(Number);
        correctAnswer = Math.floor((num1 * numerator) / denominator);
        question = `${frac} of ${num1}`;
        break;
    }

    const answers = [correctAnswer];
    while(answers.length < 4) {
      const wrong = correctAnswer + Math.floor(Math.random() * 20) - 10;
      if(wrong !== correctAnswer && !answers.includes(wrong) && wrong > 0) {
        answers.push(wrong);
      }
    }

    return {
      question,
      answers: answers.sort(() => Math.random() - 0.5),
      correct: correctAnswer
    };
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setLives(3);
    setDistance(0);
    setSpeed(5);
    setCarPosition(50);
    setObstacles([]);
    setCurrentQuestion(generateQuestion());
  };

  useEffect(() => {
    if(gameState === 'playing') {
      gameLoopRef.current = setInterval(() => {
        setDistance(d => d + 1);
        
        setObstacles(prev => {
          const updated = prev.map(obs => ({
            ...obs,
            y: obs.y + speed
          })).filter(obs => obs.y < 100);

          if(Math.random() < 0.02) {
            const newObs = {
              x: Math.random() * 80 + 10,
              y: -10,
              answer: currentQuestion?.answers[Math.floor(Math.random() * 4)]
            };
            updated.push(newObs);
          }

          return updated;
        });

        obstacles.forEach(obs => {
          if(Math.abs(obs.x - carPosition) < 8 && obs.y > 75 && obs.y < 85) {
            if(obs.answer === currentQuestion?.correct) {
              setScore(s => s + 100);
              setSpeed(sp => Math.min(sp + 0.5, 15));
              setCurrentQuestion(generateQuestion());
              setObstacles(prev => prev.filter(o => o !== obs));
            } else {
              setLives(l => {
                const newLives = l - 1;
                if(newLives <= 0) {
                  setGameState('gameover');
                }
                return newLives;
              });
              setObstacles(prev => prev.filter(o => o !== obs));
            }
          }
        });

        if(distance % 500 === 0 && distance > 0) {
          setLevel(l => l + 1);
        }
      }, 50);

      return () => clearInterval(gameLoopRef.current);
    }
  }, [gameState, speed, obstacles, carPosition, currentQuestion, distance]);

  const handleKeyPress = (e) => {
    if(gameState !== 'playing') return;
    
    if(e.key === 'ArrowLeft') {
      setCarPosition(pos => Math.max(10, pos - 5));
    } else if(e.key === 'ArrowRight') {
      setCarPosition(pos => Math.min(90, pos + 5));
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-800 to-gray-900 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-yellow-400">
          🏎️ Speed Racer Math 🏁
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏎️</div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Ready to Race?</h2>
            <p className="text-xl mb-8 text-gray-600">
              Drive your car and collect the correct answers!<br/>
              Use ← → arrow keys to steer
            </p>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START RACE
            </button>
          </div>
        )}

        {gameState === 'playing' && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Speed: {speed.toFixed(1)}x</div>
              <div className="text-xl font-bold text-red-600">
                Lives: {'❤️'.repeat(lives)}
              </div>
            </div>

            {currentQuestion && (
              <div className="bg-blue-500 text-white rounded-lg p-6 mb-4 text-center">
                <div className="text-3xl font-bold mb-2">Collect the answer:</div>
                <div className="text-5xl font-bold">{currentQuestion.question} = ?</div>
              </div>
            )}

            <div className="relative bg-gray-700 rounded-lg overflow-hidden" style={{ height: '500px' }}>
              {/* Road markings */}
              <div className="absolute inset-0">
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-12 bg-white left-1/2 transform -translate-x-1/2"
                    style={{
                      top: `${(i * 60 + (distance % 60))}px`,
                      opacity: 0.5
                    }}
                  />
                ))}
              </div>

              {/* Obstacles with answers */}
              {obstacles.map((obs, idx) => (
                <div
                  key={idx}
                  className={`absolute w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-xl transform transition-all ${
                    obs.answer === currentQuestion?.correct 
                      ? 'bg-green-500 shadow-lg shadow-green-300' 
                      : 'bg-red-500 shadow-lg shadow-red-300'
                  }`}
                  style={{
                    left: `${obs.x}%`,
                    top: `${obs.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {obs.answer}
                </div>
              ))}

              {/* Player car */}
              <div
                className="absolute bottom-16 w-12 h-20 bg-gradient-to-b from-blue-400 to-blue-600 rounded-lg transform transition-all duration-100 shadow-xl"
                style={{
                  left: `${carPosition}%`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-blue-300 rounded"></div>
                <div className="absolute bottom-1 left-1 w-2 h-4 bg-black rounded"></div>
                <div className="absolute bottom-1 right-1 w-2 h-4 bg-black rounded"></div>
              </div>
            </div>

            <div className="text-center mt-4 text-white text-lg">
              Use ← → Arrow Keys to Move
            </div>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏁</div>
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Race Over!</h2>
            <div className="text-3xl mb-4 text-blue-600 font-bold">Final Score: {score}</div>
            <div className="text-2xl mb-8 text-gray-600">Level Reached: {level}</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              RACE AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeedRacerMath;
